"use client";

import { Box } from "@chakra-ui/react";
import { useSearchParams } from "next/navigation";
import { useClassGet, useTenantData } from "@/hooks";
import { useThemeTokens } from "@/lib/theme/tokens";
import { parseClassIdFromAuthCallback } from "@/lib/utils/auth/postAuthRedirect";
import { useTenantContentSettingsGet } from "@/features/profile-settings/hooks/useTenantContentSettingsGet";
import { Text as UiText } from "@/components/ui";

interface ClassRegistrationAuthBannerProps {
  mode?: "sign-in" | "sign-up";
}

export const ClassRegistrationAuthBanner = ({
  mode = "sign-in",
}: ClassRegistrationAuthBannerProps) => {
  const theme = useThemeTokens();
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get("callbackUrl");
  const classId = parseClassIdFromAuthCallback(callbackUrl);
  const { tenantId } = useTenantData();
  const { data: contentSettings } = useTenantContentSettingsGet(
    { tenantId },
    { enabled: !!tenantId },
  );

  const { data: classData } = useClassGet(classId ?? 0, {
    enabled: classId != null && classId > 0,
  });

  if (!classId || !classData?.name) return null;

  const template =
    mode === "sign-up"
      ? contentSettings.classRegistrationAuthSignUp
      : contentSettings.classRegistrationAuthSignIn;
  const className = classData.name;
  const [before = "", after = ""] = template.includes("{className}")
    ? template.split("{className}")
    : [template, ""];

  return (
    <Box
      mb={4}
      p={4}
      borderRadius="md"
      borderWidth="1px"
      borderColor={theme.borderGlass.default}
      bg={theme.accent.primaryBg}
    >
      <UiText fontSize="sm" color={theme.text.primary} lineHeight="1.5">
        {before}
        {template.includes("{className}") ? (
          <UiText as="span" fontWeight="600">
            {className}
          </UiText>
        ) : null}
        {after}
      </UiText>
    </Box>
  );
};
